// Ported from field-rep-frontend's label explorer (Vue):
// src/internal/views/label-explorer/TrackFrameViewer.vue
// The previews, stand-ins and full frame work the same. But pan/zoom and framing
// are this app's own logic (react-zoom-pan-pinch, centred on the highlight). And
// unlike the original version one, it has no slider: the component using it draws
// its own and passes in the frame to show (framePosition).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";
import { IconPhoto } from "@tabler/icons-react";
import { MISSING_IMAGE_PLACEHOLDER } from "@/config/constants";
import { TrackFrame } from "@/types/trackLabeling";
import { frameImageCache, resolveCachedImageUrl } from "@/utils/imageCache";
import {
  getTrackFrameOverlayBox,
  TRACK_FRAME_OVERLAY_BOX_STYLE,
} from "./trackFrameOverlay";
import {
  DEFAULT_IMAGE_ADJUSTMENTS,
  imageAdjustmentsFilter,
} from "./imageAdjustments";
import { ImageAdjustmentsControl } from "./ImageAdjustmentsControl";
import { useFrameCache } from "./useFrameCache";

type TrackFrameViewerProps = {
  frames: TrackFrame[];
  /** 1-based, like the rest of the labeling page. */
  framePosition: number;
  /** Closer to the highlight, and without the shading around it, once the tracking is confirmed. */
  isTrackingConfirmed: boolean;
};

const PRE_CONFIRM_SCALE = 1.2;
const POST_CONFIRM_SCALE = 2.5;
/** How long the slider rests before the full frame replaces the preview. */
const SETTLE_MS = 150;

const FrameLoadingOverlay = () => (
  <div className="absolute inset-0 z-20 flex items-center justify-center bg-white">
    <IconPhoto size={80} className="text-[#CBD5E1]" />
  </div>
);

/**
 * A track's frames, one at a time, zoomed on the highlight. Draws small previews decoded once in
 * the background (useFrameCache), so scrubbing keeps up with the slider; the nearest ready frame
 * stands in, dimmed, while the right one loads, and the full frame replaces the preview once the
 * slider rests, so zooming in stays sharp.
 */
export const TrackFrameViewer = ({
  frames,
  framePosition,
  isTrackingConfirmed,
}: TrackFrameViewerProps) => {
  const targetIndex = Math.min(
    Math.max(framePosition - 1, 0),
    Math.max(frames.length - 1, 0),
  );
  const cache = useFrameCache(frames, targetIndex);

  // The frame on screen: the target's preview, else the nearest ready one, else (a target that
  // failed with nothing else loaded) the target itself, drawn empty.
  let shownIndex: number | null = null;
  if (cache.previewAt(targetIndex)) {
    shownIndex = targetIndex;
  } else {
    shownIndex = cache.nearestPreviewIndex(targetIndex);
    if (shownIndex === null && cache.hasSettled(targetIndex)) {
      shownIndex = targetIndex;
    }
  }
  const shownPreview =
    shownIndex === null ? undefined : cache.previewAt(shownIndex);
  const isStandIn = shownIndex !== null && shownIndex !== targetIndex;
  const isTargetSettled = cache.hasSettled(targetIndex);
  const frameSize = cache.frameSize;

  // Image adjustment tool for every frame of the track. The tool is remounted for each track,
  // so a new track restores the default settings.
  const [imageAdjustments, setImageAdjustments] = useState(
    DEFAULT_IMAGE_ADJUSTMENTS,
  );
  const frameFilter = imageAdjustmentsFilter(imageAdjustments);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const activeScale = isTrackingConfirmed
    ? POST_CONFIRM_SCALE
    : PRE_CONFIRM_SCALE;

  // Viewport size, kept up to date so the framing below re-runs when the viewer is resized.
  const [viewportSize, setViewportSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const observer = new ResizeObserver(() =>
      setViewportSize({
        width: viewport.offsetWidth,
        height: viewport.offsetHeight,
      }),
    );
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  // Draws the shown frame's preview, or clears the canvas (e.g. a frame that failed to load), so
  // the previous frame never sits under this one's highlight.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) {
      return;
    }

    if (!shownPreview) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    if (
      canvas.width !== shownPreview.width ||
      canvas.height !== shownPreview.height
    ) {
      canvas.width = shownPreview.width;
      canvas.height = shownPreview.height;
    }
    context.drawImage(shownPreview, 0, 0);
  }, [shownPreview]);

  // The full frame replaces the preview once the slider has rested on a frame that's ready: sharp
  // at any zoom. It comes from the file the cache already downloaded when there is one, so it only
  // needs decoding. Otherwise (frames loaded through an <img>) from the signed link. A stand-in
  // never gets one, as it would compete with the frame being waited for, and neither does a frame
  // shown before the frame size is known, as it has no box to fill yet.
  const shownFileLink =
    shownIndex === null ? undefined : cache.fullFrameUrlAt(shownIndex);
  const [fullFrame, setFullFrame] = useState<{
    index: number;
    url: string;
  } | null>(null);
  useEffect(() => {
    setFullFrame(null);
    const frame = shownIndex === null ? undefined : frames[shownIndex];
    if (
      shownIndex === null ||
      !frame ||
      !frameSize ||
      isStandIn ||
      !isTargetSettled ||
      frame.imageUrl === MISSING_IMAGE_PLACEHOLDER
    ) {
      return;
    }

    const index = shownIndex;
    const url =
      shownFileLink ??
      resolveCachedImageUrl(frameImageCache, frame.fileName, frame.imageUrl);
    const image = new Image();
    let isWanted = true;
    const timer = setTimeout(async () => {
      image.src = url;
      try {
        await image.decode();
      } catch {
        return;
      }
      if (isWanted) {
        setFullFrame({ index, url });
      }
    }, SETTLE_MS);
    // Cancels the full frame when the slider moves on or the viewer goes away: stops the timer, and
    // the download (if any) and decode of the 4K frame if they've started.
    return () => {
      isWanted = false;
      clearTimeout(timer);
      image.src = "";
    };
  }, [
    frames,
    frameSize,
    shownIndex,
    shownFileLink,
    isStandIn,
    isTargetSettled,
  ]);

  const overlayBox = useMemo(
    () =>
      shownIndex === null
        ? null
        : getTrackFrameOverlayBox(frames[shownIndex]?.points ?? []),
    [frames, shownIndex],
  );

  // Centres the shown frame's highlight at the active scale whenever the frame, the scale or the
  // viewer's size changes; the frame fills the viewer's width.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!overlayBox || !frameSize || !viewport || !transformRef.current) {
      return;
    }

    const viewportWidth = viewport.offsetWidth;
    const viewportHeight = viewport.offsetHeight;
    const contentHeight = viewportWidth * (frameSize.height / frameSize.width);
    const boxCenterX =
      ((overlayBox.left + overlayBox.width / 2) / 100) * viewportWidth;
    const boxCenterY =
      ((overlayBox.top + overlayBox.height / 2) / 100) * contentHeight;
    const x = viewportWidth / 2 - boxCenterX * activeScale;
    const y = viewportHeight / 2 - boxCenterY * activeScale;
    // Clamped so the frame always fills the viewer (no empty bars).
    const clampedX = Math.min(
      0,
      Math.max(viewportWidth - viewportWidth * activeScale, x),
    );
    const clampedY = Math.min(
      0,
      Math.max(viewportHeight - contentHeight * activeScale, y),
    );
    transformRef.current.setTransform(clampedX, clampedY, activeScale, 0);
  }, [overlayBox, frameSize, activeScale, viewportSize]);

  const isFullFrameShown =
    fullFrame !== null && fullFrame.index === shownIndex && !isStandIn;

  return (
    <div
      ref={viewportRef}
      className="relative h-full w-full overflow-hidden bg-[#111624]"
    >
      {!shownPreview && !isTargetSettled && <FrameLoadingOverlay />}
      <div className="absolute right-3 top-3 z-30">
        <ImageAdjustmentsControl
          adjustments={imageAdjustments}
          onChange={setImageAdjustments}
        />
      </div>
      <TransformWrapper
        ref={transformRef}
        initialScale={activeScale}
        minScale={1}
        maxScale={4}
        limitToBounds={true}
        // smoothStep scales zoom by raw wheel deltaY; bumped from default 0.001
        // so trackpad's tiny deltas zoom at a usable speed (step is ignored while smooth is on)
        wheel={{ step: 0.2, smoothStep: 0.005 }}
        pinch={{ step: 5 }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{ width: "100%" }}
        >
          <div
            className={`relative w-full transition-opacity ${isStandIn ? "opacity-50" : ""}`}
            style={{
              aspectRatio: frameSize
                ? `${frameSize.width} / ${frameSize.height}`
                : undefined,
            }}
          >
            <canvas
              ref={canvasRef}
              className="block h-full w-full"
              style={{ filter: frameFilter }}
            />
            {isFullFrameShown && (
              <img
                src={fullFrame.url}
                alt={`Frame ${fullFrame.index + 1}`}
                className="absolute inset-0 block h-full w-full"
                style={{ filter: frameFilter }}
                draggable={false}
              />
            )}
            {overlayBox && (
              <div
                className="pointer-events-none absolute z-10"
                style={{
                  left: `${overlayBox.left}%`,
                  top: `${overlayBox.top}%`,
                  width: `${overlayBox.width}%`,
                  height: `${overlayBox.height}%`,
                  ...TRACK_FRAME_OVERLAY_BOX_STYLE,
                  // The shading around the highlight is a tracking-review aid; keep the outline
                  // once confirmed but drop the dimming.
                  ...(isTrackingConfirmed ? { boxShadow: "none" } : null),
                }}
              />
            )}
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};
